import { DsIcon } from '@descomplica/design-system-react';
import { useViewModel } from '@descomplica/microfrontend-react';
import { observer } from 'mobx-react';
import React from 'react';
import styled from 'styled-components';

import { FullPageError } from '@/shared/presentation/components/FullPageError';
import { FullPageLoading } from '@/shared/presentation/components/FullPageLoading';

import ApplicationIconSvg from '../assets/icons/cubo.svg';
import { HomeViewModel } from './Home.vm';

const Container = styled.div`
  display: flex;
  flex-direction: column;

  @media screen and (max-width: 480px) {
    padding: 0 16px;
  }
`;

const WelcomeWrapper = styled.div`
  font-style: normal;
  font-weight: 800;
  font-size: 48px;
  line-height: 100%;
  letter-spacing: -1.2px;
  margin-bottom: 72px;
  display: flex;
  align-items: flex-start;
  gap: 16px;

  @media screen and (max-width: 480px) {
    font-size: 32px;
    line-height: 120%;
    letter-spacing: -0.8px;
    margin-bottom: 32px;
    margin-top: 16px;
  }
`;

const WelcomeMessage = styled.p`
  display: inline;
  margin: 0;
  padding: 0;
`;

const List = styled.ul`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  list-style: none;
  margin: 0;
  padding: 0;
  max-width: 100%;

  @media screen and (max-width: 800px) {
    justify-content: center;
    display: grid;
    grid-template-columns: repeat(1, 1fr);
  }
`;

const Item = styled.li`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 48px;
  background: var(--gray-90);
  border-radius: 6px;
  min-height: 360px;
  cursor: pointer;
  transition: all 0.15s;

  @media screen and (max-width: 800px) {
    min-width: 80%;
  }

  @media screen and (max-width: 480px) {
    padding: 0 16px;
    width: 100%;
    max-width: initial;
    min-width: initial;
  }

  &:hover {
    scale: 1.025;
  }
`;

const ApplicationInfos = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const ApplicationIcon = styled.img`
  width: auto;
  height: 64px;
  margin-bottom: 16px;
`;

const ApplicationName = styled.h2`
  font-weight: 700;
  font-size: 24px;
  line-height: 120%;
  color: rgb(255, 255, 255);
  padding: 0px;
  margin: 0px;
`;

const ApplicationCompany = styled.p`
  font-weight: 400;
  font-size: 16px;
  line-height: 120%;
  color: rgb(255, 255, 255, 0.5);
  padding: 0px;
  margin: 0px;
`;

const ApplicationActions = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
`;

const ActionButton = styled.button`
  border: 0;
  padding: 0;
  background: transparent;
  display: flex;
  width: 50px;
  height: 50px;
  border-radius: 50px;
  align-items: center;
  justify-content: center;
  border: 1px solid #313335;
  cursor: pointer;
  z-index: 0;

  & > .ds-icon {
    background-color: #caccce;
  }

  &:hover {
    filter: brightness(150%);
  }
`;

export const HomeScreen = observer(() => {
  const vm = useViewModel(HomeViewModel);

  if (vm.isLoading || !vm.user) {
    return <FullPageLoading />;
  }

  if (vm.hasError) {
    return <FullPageError errorMessage="Erro ao carregar as aplicações" />;
  }

  return (
    <Container>
      <WelcomeWrapper>
        <div aria-hidden="true">{vm.greetingMessage.icon}</div>{' '}
        <WelcomeMessage>
          {vm.greetingMessage.message}, {vm.user.name}
        </WelcomeMessage>
      </WelcomeWrapper>

      <List>
        {!vm.applications.length && !vm.isLoading && !vm.hasError && (
          <div>Você não possui aplicações</div>
        )}
        {vm.applications.map((application, index) => (
          <Item
            key={index}
            onClick={() => {
              vm.openApplication(application);
            }}
          >
            <ApplicationInfos>
              {application.organization.logo ? (
                <ApplicationIcon src={application.organization.logo} alt="" />
              ) : (
                <ApplicationIcon src={ApplicationIconSvg} alt="" />
              )}
              <ApplicationName>{application.name}</ApplicationName>
              <ApplicationCompany>{application.organization.name}</ApplicationCompany>
            </ApplicationInfos>

            <ApplicationActions>
              {application.canViewControlPanelAdmin && (
                <ActionButton
                  onClick={(e) => {
                    e.stopPropagation();
                    vm.openApplicationAdmin(application);
                  }}
                >
                  <DsIcon image={'settings'} />
                </ActionButton>
              )}

              <ActionButton
                onClick={(e) => {
                  e.stopPropagation();
                  vm.openApplication(application);
                }}
              >
                <DsIcon image={'arrow-forward'} />
              </ActionButton>
            </ApplicationActions>
          </Item>
        ))}
      </List>
    </Container>
  );
});
